import { useState, useCallback, useRef } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useResizeDetector } from 'react-resize-detector'
import cn from 'classnames'
import { resetErrorMessage } from 'actions/errors'
import { selectLoggedIn } from 'selectors/auth'
import { selectEdit } from 'selectors/graph'
import { isEmbeddedReadOnly } from 'lib/embed'
import { useAppSelector, useAppDispatch } from 'store/hooks'
import type { RootState } from 'types/state'
import Graph from 'containers/Graph'
import Login from 'containers/Login'
import Signup from 'containers/Signup'
import ResetPasswordForm from 'components/ResetPasswordForm'
import Logout from 'containers/Logout'
import Header from 'components/Header'
import Footer from 'components/Footer'
import Confirm from 'containers/Confirm'
import CloseButton from 'components/utils/CloseButton'

import 'styles/main.less'
import 'containers/App.less'

const selectErrors = (state: RootState) =>
  ((state as Record<string, unknown>).errors as { errors?: string[] })?.errors ?? []

const App = () => {
  const errors = useAppSelector(selectErrors)
  const edit = useAppSelector(selectEdit)
  const { loggedIn } = useAppSelector(selectLoggedIn)
  const dispatch = useAppDispatch()
  const [width, setWidth] = useState(0)
  const [height, setHeight] = useState(0)
  const appContentRef = useRef<HTMLDivElement>(null)

  const onResize = useCallback((w?: number, h?: number) => {
    setWidth(w ?? 0)
    setHeight(h ?? 0)
  }, [])

  useResizeDetector({ targetRef: appContentRef, onResize })

  const { isEditing } = edit

  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <div
        className={cn('app', {
          unauthenticated: !loggedIn,
          notEditing: !isEditing,
        })}
      >
        <Confirm />
        {/* <Header /> */}
        {errors.length > 0 && (
          <div>
            {errors.map((err: string, i: number) => (
              <div className="error" key={`error-${i}`}>
                <div>
                  <b>Error</b>: {err}
                </div>
                <CloseButton onClick={() => dispatch(resetErrorMessage(i))} />
              </div>
            ))}
          </div>
        )}
        <div className={cn('app-content')} ref={appContentRef}>
          <Routes>
            <Route path="/" element={<Navigate to="/graph" replace />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/logout" element={<Logout />} />
            <Route path="/resetPassword" element={<ResetPasswordForm />} />
            <Route path="/graph" element={
              <Graph width={width} height={height} />
            } />
          </Routes>
        </div>
        {!isEmbeddedReadOnly ? <Footer /> : null}
      </div>
    </BrowserRouter>
  )
}

export default App
